import { useState } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { get } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/layout/PageHeader';
import { Thumbnail } from '@/components/domain/Thumbnail';
import { formatRelative } from '@/lib/time';
import { genderLabel, resolveStoreGender } from '@/lib/store-gender';
import { storePath } from '@/lib/store';

type TeamStore = {
  id: string;
  status?: string;
  schoolName?: string;
  organizationName?: string;
  storeNumberDisplay?: string;
  sportType?: string;
  sport_id?: string;
  gender?: string | null;
  sport_gender?: string | null;
  submittedAt?: string;
  updatedAt?: string;
  logoPath?: string;
  logos?: { gcsPath?: string }[];
};
type ListPayload = { stores: TeamStore[] };

// Everything an operator can still act on before the store builds: the prep
// statuses, plus the proof road (rendering → proof_sent → changes_requested /
// proof_email_failed). Before 2026-09-16 the proof statuses were missing here,
// so a store vanished from this queue the moment its proof was sent.
const QUEUE_STATUSES = [
  'pending_review', 'pending_artwork', 'artwork_ready', 'artwork_failed', 'shopify_error',
  'rendering_proof', 'proof_sent', 'proof_email_failed', 'changes_requested',
] as const;
const PROOF_ROAD = 'rendering_proof,proof_sent,proof_email_failed,changes_requested';

const STATUS_CHIPS = [
  { value: '',                   label: 'All' },
  { value: 'pending_review',     label: 'Pending Review' },
  { value: 'pending_artwork',    label: 'Pending Artwork' },
  { value: 'artwork_ready',      label: 'Artwork Ready' },
  { value: 'artwork_failed',     label: 'Artwork Failed' },
  { value: PROOF_ROAD,           label: 'Proof road' },
  { value: 'proof_sent',         label: 'Proof Sent' },
  { value: 'changes_requested',  label: 'Changes Requested' },
  { value: 'proof_email_failed', label: 'Proof Email Failed' },
  { value: 'shopify_error',      label: 'Shopify Error' },
] as const;

const STATUS_BADGE: Record<string, string> = {
  pending_review:     'bg-bg text-muted',
  pending_artwork:    'bg-warning/10 text-warning',
  artwork_ready:      'bg-primary/10 text-primary',
  artwork_failed:     'bg-danger/10 text-danger',
  shopify_error:      'bg-danger/10 text-danger',
  rendering_proof:    'bg-primary/10 text-primary',
  proof_sent:         'bg-success/10 text-success',
  proof_email_failed: 'bg-danger/10 text-danger',
  changes_requested:  'bg-warning/10 text-warning',
};

export default function TeamStoreSubmissions() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<string>('');
  const [q, setQ] = useState('');

  const statusParam = filter || QUEUE_STATUSES.join(',');
  const url = `/api/active-stores?limit=2000&status=${statusParam}`;
  const { data, isLoading } = useSWR<ListPayload>(url, get);

  // Open change requests per store — same source as the Revisions queue, so
  // the two pages agree on what is outstanding.
  const { data: reqCounts, error: reqCountsError } = useSWR<{ counts: Record<string, { count: number; latestNote: string }> }>(
    '/api/revisions/store-counts', get,
  );

  const filtered = (data?.stores || []).filter((s) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      String(s.schoolName || s.organizationName || '').toLowerCase().includes(needle) ||
      String(s.storeNumberDisplay || '').toLowerCase().includes(needle)
    );
  });

  return (
    <div className="px-7 py-6">
      <PageHeader
        title="Team Store Submissions"
        subtitle={`Stores in review — pending, artwork, proof road, or recovery · ${filtered.length}${q || filter ? ` of ${data?.stores.length ?? 0}` : ''} shown — open the full Store page`}
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_CHIPS.map((c) => (
            <button
              key={c.value || 'all'}
              onClick={() => setFilter(c.value)}
              className={`rounded-full px-3 py-1 text-[11.5px] font-medium transition-colors ${filter === c.value ? 'bg-text text-white' : 'border border-black/[0.08] bg-surface text-muted hover:bg-bg'}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by school, store #…" className="max-w-[260px] flex-1" />
      </div>

      {reqCountsError ? <p className="mb-2 text-[11.5px] text-muted">Open change-request counts couldn't load — badges may be missing. Check the Revisions page.</p> : null}

      <div className="overflow-hidden rounded-[12px] border border-black/[0.05] bg-surface shadow-card">
        {isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : filtered.length ? (
          filtered.map((s) => (
            <button
              key={s.id}
              onClick={() => navigate(storePath(s.id))}
              className="flex w-full items-center gap-3 border-b border-divider px-3.5 py-2.5 text-left transition-colors last:border-b-0 hover:bg-bg/60"
            >
              <Thumbnail path={s.logos?.find((l) => l?.gcsPath)?.gcsPath || s.logoPath} alt="store logo" className="h-8 w-8" />
              <span className="w-20 shrink-0 font-mono text-[10px] text-subtle">
                {s.storeNumberDisplay || s.id.slice(0, 12)}
              </span>
              <span className="flex-1 truncate text-[12.5px]">
                {s.schoolName || s.organizationName || 'Unknown'}
              </span>
              <span className="w-12 shrink-0 capitalize text-[11px] text-muted">
                {genderLabel(resolveStoreGender(s))}
              </span>
              {reqCounts?.counts?.[s.id] ? (
                <Badge className="bg-warning/10 text-warning" title={reqCounts.counts[s.id].latestNote}>
                  {reqCounts.counts[s.id].count} open
                </Badge>
              ) : null}
              <Badge className={STATUS_BADGE[s.status ?? ''] ?? 'bg-bg text-muted'}>{s.status || '?'}</Badge>
              <span className="w-12 shrink-0 text-right text-[11px] text-muted">
                {s.submittedAt ? formatRelative(s.submittedAt) : ''}
              </span>
            </button>
          ))
        ) : (
          <div className="p-10 text-center text-[12px] text-muted">Nothing in this queue. ✓</div>
        )}
      </div>

    </div>
  );
}
